import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import CONFIG from '../config';
import { getToken } from '../utils/auth';
import BRANDING from '../branding';

const WishlistContext = createContext();

export const WishlistProvider = ({ children }) => {
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const isWishlistEnabled = BRANDING.features?.wishlist !== false;

    const fetchWishlist = useCallback(async () => {
        const token = getToken();
        if (!token || !isWishlistEnabled) return;

        try {
            setLoading(true);
            const res = await axios.get(`${CONFIG.API_URL}/api/wishlist`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setWishlistItems(res.data);
        } catch (err) {
            console.error('Failed to fetch wishlist:', err);
        } finally {
            setLoading(false);
        }
    }, [isWishlistEnabled]);

    useEffect(() => {
        if (isWishlistEnabled) {
            const token = getToken();
            if (token) {
                fetchWishlist();
            } else {
                const localWishlist = localStorage.getItem('guest_wishlist');
                if (localWishlist) {
                    try {
                        setWishlistItems(JSON.parse(localWishlist));
                    } catch (e) {
                        setWishlistItems([]);
                    }
                }
            }
        }
    }, [fetchWishlist, isWishlistEnabled]);

    const toggleWishlist = async (product) => {
        const token = getToken();
        console.log('❤️ toggleWishlist triggered:', { productId: product?.id, hasToken: !!token });
        if (token) {
            try {
                console.log('📡 Sending wishlist toggle request...');
                const res = await axios.post(`${CONFIG.API_URL}/api/wishlist/toggle`, 
                    { productId: product.id },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                console.log('✅ Wishlist toggle success:', res.data);
                await fetchWishlist();
                console.log('✅ Wishlist refreshed');
                return res.data.added;
            } catch (err) {
                console.error('❌ Toggle wishlist failed:', err.response?.data || err.message);
                console.error('Full error:', err);
            }
        } else {
            console.log('👤 Guest user - using localStorage');
            let added = false;
            setWishlistItems(prev => {
                const existing = prev.find(item => item.product_id === product.id);
                let newWishlist;
                if (existing) {
                    newWishlist = prev.filter(item => item.product_id !== product.id);
                    added = false;
                    console.log('✅ Removed from guest wishlist');
                } else {
                    newWishlist = [...prev, { ...product, product_id: product.id }];
                    added = true;
                    console.log('✅ Added to guest wishlist');
                }
                localStorage.setItem('guest_wishlist', JSON.stringify(newWishlist));
                return newWishlist;
            });
            return added;
        }
    };

    const isInWishlist = (productId) => {
        return wishlistItems.some(item => item.product_id === productId);
    };

    return (
        <WishlistContext.Provider value={{ 
            wishlistItems, 
            toggleWishlist, 
            isInWishlist,
            loading,
            refreshWishlist: fetchWishlist 
        }}>
            {children}
        </WishlistContext.Provider>
    );
};

export const useWishlist = () => useContext(WishlistContext);
